/* ============================================================
   TEKDECOR — behavior layer
   Scroll reveal, header states, marquees, counters, quick-add,
   product gallery, lightbox. No dependencies.
   ============================================================ */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------
     Scroll reveal — .td-reveal elements rise + fade on entry
     ---------------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.td-reveal');
    if (!els.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------
     Header — transparent over hero, solid + blur after scroll
     ---------------------------------------------------------- */
  function initHeader() {
    var wrapper = document.querySelector('.section-header');
    if (!wrapper) return;
    var hasHero = !!document.querySelector('.td-hero3d');
    document.body.classList.toggle('td-has-hero', hasHero);

    var update = function () {
      var scrolled = window.scrollY > 24;
      document.body.classList.toggle('td-scrolled', scrolled);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ----------------------------------------------------------
     Marquee — duplicate content until track is 2x viewport
     ---------------------------------------------------------- */
  function initMarquees() {
    document.querySelectorAll('.td-marquee__track').forEach(function (track) {
      if (track.dataset.tdFilled) return;
      track.dataset.tdFilled = '1';
      var content = track.innerHTML;
      var guard = 0;
      while (track.scrollWidth < window.innerWidth * 2 && guard < 10) {
        track.innerHTML += content;
        guard++;
      }
      track.innerHTML += track.innerHTML;
      track.classList.add('is-ready');
    });
  }

  /* ----------------------------------------------------------
     Animated counters — [data-td-count] elements
     ---------------------------------------------------------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-td-count]');
    if (!els.length) return;
    var animate = function (el) {
      var target = parseFloat(el.getAttribute('data-td-count'));
      var decimals = (String(el.getAttribute('data-td-count')).split('.')[1] || '').length;
      var dur = 900;
      var start = null;
      var step = function (ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = (target * eased).toFixed(decimals);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.textContent = el.getAttribute('data-td-count');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------
     Quick add — data-td-quick-add="<variant_id>"
     Adds to cart, refreshes + opens Dawn's cart drawer.
     ---------------------------------------------------------- */
  function refreshCartDrawer(openAfter) {
    var sections = 'cart-drawer,cart-icon-bubble';
    return fetch(window.location.pathname + '?sections=' + sections)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var drawerHtml = data['cart-drawer'];
        var bubbleHtml = data['cart-icon-bubble'];
        if (bubbleHtml) {
          var bubbleTarget = document.getElementById('cart-icon-bubble');
          if (bubbleTarget) {
            var t = document.createElement('div');
            t.innerHTML = bubbleHtml;
            bubbleTarget.innerHTML = t.querySelector('#shopify-section-cart-icon-bubble')
              ? t.querySelector('#shopify-section-cart-icon-bubble').innerHTML
              : bubbleHtml;
            bubbleTarget.classList.add('td-cart-pop');
            setTimeout(function () { bubbleTarget.classList.remove('td-cart-pop'); }, 450);
          }
        }
        var drawer = document.querySelector('cart-drawer');
        if (drawer && drawerHtml) {
          var tmp = document.createElement('div');
          tmp.innerHTML = drawerHtml;
          var fresh = tmp.querySelector('cart-drawer');
          if (fresh) {
            drawer.querySelector('.drawer__inner').innerHTML = fresh.querySelector('.drawer__inner').innerHTML;
            drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          }
          if (openAfter && typeof drawer.open === 'function') drawer.open();
          else if (openAfter) drawer.classList.add('active', 'animate');
        }
      });
  }

  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-td-quick-add]');
      if (!btn) return;
      e.preventDefault();
      if (btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      var original = btn.innerHTML;
      btn.innerHTML = '<span class="td-btn-spinner" aria-hidden="true"></span><span class="visually-hidden">Adding…</span>';
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: parseInt(btn.getAttribute('data-td-quick-add'), 10), quantity: 1 }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () { return refreshCartDrawer(true); })
        .then(function () {
          btn.innerHTML = 'Added ✓';
          setTimeout(function () { btn.innerHTML = original; }, 1400);
        })
        .catch(function () {
          btn.innerHTML = 'Error — retry';
          setTimeout(function () { btn.innerHTML = original; }, 2000);
        })
        .finally(function () { btn.classList.remove('is-loading'); });
    });
  }

  /* ----------------------------------------------------------
     Product gallery — vertical thumbs, click to swap,
     hover zoom, lightbox
     ---------------------------------------------------------- */
  function initGallery() {
    var gallery = document.querySelector('[data-td-gallery]');
    if (!gallery || gallery.dataset.tdGalleryInit) return;
    gallery.dataset.tdGalleryInit = '1';
    var main = gallery.querySelector('[data-td-gallery-main] img');
    var zoomWrap = gallery.querySelector('[data-td-gallery-main]');
    var thumbs = gallery.querySelectorAll('[data-td-thumb]');
    var lightbox = document.querySelector('[data-td-lightbox]');
    var lightboxImg = lightbox ? lightbox.querySelector('img') : null;
    var current = 0;

    function activate(idx) {
      var thumb = thumbs[idx];
      if (!thumb || !main) return;
      current = idx;
      var full = thumb.getAttribute('data-td-full');
      var alt = thumb.getAttribute('data-td-alt') || '';
      main.classList.add('is-swapping');
      var pre = new Image();
      pre.onload = function () {
        main.src = full;
        main.srcset = '';
        main.alt = alt;
        main.classList.remove('is-swapping');
      };
      pre.src = full;
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === idx);
        t.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { activate(i); });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(i); }
      });
    });

    if (zoomWrap && window.matchMedia('(hover: hover)').matches && !reducedMotion) {
      zoomWrap.addEventListener('mousemove', function (e) {
        var rect = zoomWrap.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        main.style.transformOrigin = x + '% ' + y + '%';
      });
      zoomWrap.addEventListener('mouseenter', function () { main.classList.add('is-zoomed'); });
      zoomWrap.addEventListener('mouseleave', function () {
        main.classList.remove('is-zoomed');
        main.style.transformOrigin = 'center';
      });
    }

    if (zoomWrap && lightbox && lightboxImg) {
      var openLightbox = function () {
        lightboxImg.src = main.src;
        lightboxImg.alt = main.alt;
        lightbox.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        lightbox.querySelector('[data-td-lightbox-close]').focus();
      };
      var closeLightbox = function () {
        lightbox.setAttribute('hidden', '');
        document.body.style.overflow = '';
        zoomWrap.focus();
      };
      zoomWrap.addEventListener('click', openLightbox);
      zoomWrap.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') openLightbox();
      });
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target.closest('[data-td-lightbox-close]')) closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !lightbox.hasAttribute('hidden')) closeLightbox();
        if (!lightbox.hasAttribute('hidden')) {
          if (e.key === 'ArrowRight') { activate((current + 1) % thumbs.length); lightboxImg.src = main.src; }
          if (e.key === 'ArrowLeft') { activate((current - 1 + thumbs.length) % thumbs.length); lightboxImg.src = main.src; }
        }
      });
    }
  }

  /* ----------------------------------------------------------
     Catalog card hover — preload secondary image
     ---------------------------------------------------------- */
  function initCardPreload() {
    document.querySelectorAll('.td-card [data-td-hover-src]').forEach(function (img) {
      var src = img.getAttribute('data-td-hover-src');
      if (src) {
        var pre = new Image();
        pre.src = src;
      }
    });
  }

  /* ---------------------------------------------------------- */
  function boot() {
    initReveal();
    initHeader();
    initMarquees();
    initCounters();
    initQuickAdd();
    initGallery();
    initCardPreload();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initMarquees();
    initCounters();
    initGallery();
  });
})();
